import { useChannelContext } from "client/components/channel"
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react"

import { useAlreadyPlayed } from "./useAlreadyPlayed"
import { MutedAutoplayProps, useAutoplay } from "./useAutoplay"
import { usePlaybackTimingSetup } from "./usePlaybackTimingSetup"
import { useSubtitlesVisible } from "./useSubtitlesVisible"
import { useVideoEventListener } from "./useVideoEventListener"
import { defaultVideoState, VideoState } from "./VideoState"

const VideoContext = createContext<VideoState | undefined>(undefined)

type VideoProviderProps = {
  children: ReactNode
  autoplayStrategy?: MutedAutoplayProps["autoplayStrategy"]
}

const VideoProvider: FC<VideoProviderProps> = ({
  children,
  autoplayStrategy,
}) => {
  const [videoElement, setVideoElement] =
    useState<HTMLVideoElement | null>(defaultVideoState.videoElement)
  const [playerError, setPlayerError] = useState(
    defaultVideoState.playerError,
  )
  const [videoSourceLoading, setVideoSourceLoading] = useState(false)
  const [subtitleBottom, setSubtitleBottom] = useState(0)

  // Sets up updatePlaybackTiming calls so we keep plugin-service informed about the video timestamp.
  usePlaybackTimingSetup(videoElement)

  // Sets up event listeners on the video element so we track the video's attributes & cause rerenders
  // when those attributes change (e.g. so our video controls ui displays the new values)
  const {
    isPlaying,
    canPlay,
    videoTimestamp,
    isSeeking,
    volume,
    prevNonZeroVolume,
    setPrevNonZeroVolume,
    isMuted,
  } = useVideoEventListener(videoElement)

  // Track `alreadyPlayed` so we know whether to show the BigPlayButton when paused (shop-mini only)
  const alreadyPlayed = useAlreadyPlayed(videoElement)

  const [subtitlesVisible, setSubtitlesVisible] = useSubtitlesVisible(
    videoElement,
    canPlay,
  )

  const { live } = useChannelContext()

  const { mutedPreview, setMutedPreview } = useAutoplay({
    videoElement,
    autoplayStrategy,
    canPlay,
    live,
  })

  // Manually pause/reset the video when channel's `live` becomes false.
  // We still receive video we just pretend it's off. If we don't pause here, the user hears the video
  // Refs https://github.com/lazarentertainment/broadcast-viewer/issues/592
  useEffect(() => {
    if (!live) {
      videoElement?.pause()
    }
  }, [videoElement, live])

  // Assemble the state to return.
  // This is what everyone who uses `useVideoContext` will receive
  const state: VideoState = {
    videoElement,
    setVideoElement,
    subtitlesVisible,
    setSubtitlesVisible,
    playerError,
    setPlayerError,
    subtitleBottom,
    setSubtitleBottom,
    setPrevNonZeroVolume,

    setVideoSourceLoading,

    mutedPreview,
    setMutedPreview,

    isPlaying,
    canPlay: !videoSourceLoading && canPlay,
    videoTimestamp,
    isSeeking,
    volume,
    prevNonZeroVolume,
    isMuted,
    alreadyPlayed,
  }

  return (
    <VideoContext.Provider value={state}>{children}</VideoContext.Provider>
  )
}

const useVideoContext = () => {
  const videoContext = useContext(VideoContext)
  if (!videoContext) {
    throw new Error(
      "No VideoContext.Provider found when calling useVideoContext.",
    )
  }
  return videoContext
}

export { VideoProvider, useVideoContext }
